/**
 * useSyncSelectedPropertyToRoute — records the property the user is currently
 * VIEWING (the `:propertyId` in a per-property /app route) into the global
 * `usePropertyStore.selectedProperty`, so every per-property consumer resolves the
 * open forest without a manual localStorage seed.
 *
 * Why here: the URL is the single source of truth for "which forest is open".
 * Only the my-forests portfolio click writes the store
 * (selectPortfolioProperty); opening a forest via forest-overview / "Avaa metsä" /
 * browse / landing / a direct link does not, so /app/watch (and other routes that
 * read useSelectedProperty → usePropertyStore) would show the empty
 * "Valitse tila" state while a forest is open. Mounting this in the shell
 * (FmShellInner, live across all /app/* navigations) fixes propagation at the
 * source for ALL per-property routes at once.
 *
 * NO-FALLBACK: only a property whose id is genuinely present in the current route
 * is recorded — never a fabricated selection. The richer selection written by the
 * portfolio flow (address/municipality/area) is preserved: we skip when the store
 * already holds this property, and only fill the gap when the store is empty or
 * points at a different forest. Missing display fields stay unset (the id is the
 * authoritative display id) rather than inventing coordinates/region/area.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePropertyStore } from '@/stores/property';
import { getCatalogRoutePropertyId } from '@/shared/ui/FmRouteCatalog';
import { fmRouteCatalogDefault } from '@/shared/ui/FmRouteCatalogDefault';

/** Pure: extract the viewed property id from a pathname, or null if the route
 *  carries no per-property context. Exported for unit coverage. */
export function extractRoutePropertyId(
  pathname: string,
  search = '',
): string | null {
  return getCatalogRoutePropertyId(fmRouteCatalogDefault, pathname, search);
}

export function useSyncSelectedPropertyToRoute(): void {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const routePropertyId = extractRoutePropertyId(pathname, search);
    if (!routePropertyId) return;

    const store = usePropertyStore.getState();
    // Already the active selection (possibly with richer portfolio fields) — leave
    // it untouched so we never downgrade address/municipality/area to the id-only
    // form.
    if (store.selectedProperty?.property_id === routePropertyId) return;

    // Fill the gap: the user is clearly viewing this forest but the store is empty
    // or points elsewhere. Record the authoritative id; downstream consumers fetch
    // the boundary / richer data. address = the id (the human-facing display id),
    // no fabricated coordinates/region/area.
    store.selectProperty({ property_id: routePropertyId, address: routePropertyId });
  }, [pathname, search]);
}
